"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

// ════════════════════════════════════════════════════════════════════
const STRINGS: Record<
  Locale,
  {
    factors: { label: string; sub: string }[];
    events: string[];
    expectationTitle: string;
    expectationDescription: string;
    price: string;
    time: string;
    reactsEachTime: string;
    gapTitle: string;
    gapDescription: string;
    earnings: string;
    actualEarnings: string;
    marketExpectation: string;
    miss: string;
    gap: string;
    missToDrop: string;
    gapBottomNote: string;
  }
> = {
  ja: {
    factors: [
      { label: "足元の業績", sub: "売上・利益" },
      { label: "事前期待", sub: "織り込み度合い" },
      { label: "外部環境", sub: "金利・為替" },
      { label: "短期の需給", sub: "ニュース・偏り" },
    ],
    events: ["好決算発表", "すでに織り込み済み", "利下げ期待", "短期的な利確売り"],
    expectationTitle: "株価は『期待の更新』で動く",
    expectationDescription: "同じ決算でも、事前期待や外部環境が違えば反応は変わる",
    price: "株価",
    time: "時間 →",
    reactsEachTime: "期待が変わるたびに株価が反応する",
    gapTitle: "好決算でも下がるのは「期待との差」",
    gapDescription: "決算の数字そのものより、事前の期待をどれだけ超えたかで株価は反応する",
    earnings: "利益",
    actualEarnings: "実際の利益",
    marketExpectation: "市場の期待",
    miss: "未達",
    gap: "差",
    missToDrop: "期待未達 → 下落",
    gapBottomNote: "同じ好決算でも、期待を超えたかどうかで株価の反応は変わる",
  },
  en: {
    factors: [
      { label: "Current results", sub: "Revenue and profit" },
      { label: "Prior expectations", sub: "How much is priced in" },
      { label: "External conditions", sub: "Rates and FX" },
      { label: "Short-term flows", sub: "News and positioning" },
    ],
    events: ["Strong earnings", "Already priced in", "Rate-cut hopes", "Short-term profit taking"],
    expectationTitle: "Share prices move on updated expectations",
    expectationDescription:
      "The same earnings report draws a different reaction depending on prior expectations and conditions",
    price: "Share price",
    time: "Time →",
    reactsEachTime: "The price reacts each time expectations change",
    gapTitle: "Strong earnings can still fall — it is the gap against expectations",
    gapDescription:
      "The price reacts to how far the result beat prior expectations, more than to the figures themselves",
    earnings: "Profit",
    actualEarnings: "Actual profit",
    marketExpectation: "Market expectation",
    miss: "Miss",
    gap: "Gap",
    missToDrop: "Missed expectations → fall",
    gapBottomNote: "Even with strong earnings, the reaction depends on whether it beat expectations",
  },
};

// 2-2 section 1: 期待の更新が株価に反映される
// ════════════════════════════════════════════════════════════════════

// SVG icon paths (lucide-style, 24x24 viewBox)
const FACTOR_ICONS = {
  barChart: "M3 3v18h18 M7 16v-4 M11 16V8 M15 16v-6 M19 16v-2",
  target: "M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0 M12 12m-5 0a5 5 0 1 0 10 0a5 5 0 1 0 -10 0 M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0",
  globe: "M12 2a10 10 0 1 0 0 20a10 10 0 1 0 0 -20 M2 12h20 M12 2a15 15 0 0 1 4 10a15 15 0 0 1 -4 10 M12 2a15 15 0 0 0 -4 10a15 15 0 0 0 4 10",
  zap: "M13 2L3 14h9l-1 8l10 -12h-9l1 -8",
} as const;

function SvgIcon({ path, x, y, size, color }: { path: string; x: number; y: number; size: number; color: string }) {
  const scale = size / 24;
  return (
    <g transform={`translate(${x}, ${y}) scale(${scale})`}>
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </g>
  );
}

export function DocPriceExpectationDiagram() {
  const t = STRINGS[useDocLocale()];
  const factors = [
    { ...t.factors[0]!, color: COLOR.sky, icon: FACTOR_ICONS.barChart },
    { ...t.factors[1]!, color: COLOR.amber, icon: FACTOR_ICONS.target },
    { ...t.factors[2]!, color: COLOR.purple, icon: FACTOR_ICONS.globe },
    { ...t.factors[3]!, color: COLOR.rose, icon: FACTOR_ICONS.zap },
  ] as const;

  const events = [
    { label: t.events[0]!, factorIdx: 0, direction: "up" as const },
    { label: t.events[1]!, factorIdx: 1, direction: "down" as const },
    { label: t.events[2]!, factorIdx: 2, direction: "up" as const },
    { label: t.events[3]!, factorIdx: 3, direction: "down" as const },
  ];

  const N = events.length;

  // ── Vertical-stacked layout (mobile-friendly) ──
  // Top: 2×2 factor card grid
  // Bottom: full-width chart with connectors from cards down to event points

  const W = 400;
  const iconSize = 16;
  const cardW = 178;
  const cardH = 44;
  const cardGapX = 12;
  const cardGapY = 10;
  const gridLeft = (W - cardW * 2 - cardGapX) / 2;
  const gridTop = 16;

  // Card positions (2×2)
  const cardPos = factors.map((_, i) => ({
    x: gridLeft + (i % 2) * (cardW + cardGapX),
    y: gridTop + Math.floor(i / 2) * (cardH + cardGapY),
  }));
  const gridBottom = gridTop + 2 * cardH + cardGapY;

  // Chart area below cards
  const chartMarginTop = 40;
  const chartLeft = 32;
  const chartRight = W - 16;
  const chartTop = gridBottom + chartMarginTop;
  const chartH = 150;
  const chartBottom = chartTop + chartH;
  const chartMidY = chartTop + chartH / 2;

  // Price line points
  const segW = (chartRight - chartLeft - 16) / (N + 0.5);
  const pts = [
    { x: chartLeft + 8, y: chartMidY + 20 },
    { x: chartLeft + 8 + segW * 1, y: chartMidY - 16 },
    { x: chartLeft + 8 + segW * 2, y: chartMidY + 12 },
    { x: chartLeft + 8 + segW * 3, y: chartMidY - 32 },
    { x: chartLeft + 8 + segW * 4, y: chartMidY - 8 },
  ];

  let fullPath = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const prev = pts[i - 1];
    const cur = pts[i];
    const cpx = (prev.x + cur.x) / 2;
    fullPath += ` C ${cpx} ${prev.y}, ${cpx} ${cur.y}, ${cur.x} ${cur.y}`;
  }
  const areaPath = `${fullPath} L ${pts[pts.length - 1].x} ${chartBottom} L ${pts[0].x} ${chartBottom} Z`;

  const pathLen = 700;
  const dur = 8;
  const viewH = chartBottom + 36;

  const tPct = (i: number) => ({
    start: (i / N) * 75,
    peak: ((i + 0.35) / N) * 75,
    hold: ((i + 0.7) / N) * 75,
    end: ((i + 1.1) / N) * 75,
  });

  const scanStart = chartLeft + 4;
  const scanEnd = chartRight - 4;

  return (
    <IllustrationCard
      title={t.expectationTitle}
      description={t.expectationDescription}
    >
      <style>{`
        @keyframes pe-scan {
          0%, 2% { transform: translateX(0px); opacity: 0; }
          5% { opacity: 1; }
          80% { transform: translateX(${scanEnd - scanStart}px); opacity: 1; }
          85%, 100% { transform: translateX(${scanEnd - scanStart}px); opacity: 0; }
        }
        @keyframes pe-draw {
          0%, 2% { stroke-dashoffset: ${pathLen}; }
          80% { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes pe-area {
          0%, 2% { clip-path: inset(0 100% 0 0); }
          80% { clip-path: inset(0 0% 0 0); }
          100% { clip-path: inset(0 0% 0 0); }
        }
        @keyframes pe-pulse {
          0%, 100% { r: 4; }
          50% { r: 6; }
        }
        ${events.map((_, i) => {
          const phase = tPct(i);
          return `
        @keyframes pe-factor${i} {
          0%, ${phase.start}% { opacity: 0.3; }
          ${phase.peak}% { opacity: 1; }
          ${phase.hold}% { opacity: 1; }
          ${phase.end}%, 100% { opacity: 0.4; }
        }
        @keyframes pe-glow${i} {
          0%, ${phase.start}% { stroke-opacity: 0; }
          ${phase.peak}% { stroke-opacity: 0.6; }
          ${phase.hold}% { stroke-opacity: 0.4; }
          ${phase.end}%, 100% { stroke-opacity: 0; }
        }
        @keyframes pe-conn${i} {
          0%, ${phase.start}% { opacity: 0; }
          ${phase.peak}% { opacity: 1; }
          ${phase.hold}% { opacity: 0.7; }
          ${phase.end}%, 100% { opacity: 0; }
        }
        @keyframes pe-evt${i} {
          0%, ${phase.start}% { opacity: 0; transform: scale(0.85); }
          ${phase.peak}% { opacity: 1; transform: scale(1); }
          ${phase.hold}% { opacity: 1; transform: scale(1); }
          ${phase.end}%, 100% { opacity: 0; transform: scale(0.85); }
        }
        @keyframes pe-ring${i} {
          0%, ${phase.start}% { r: 0; opacity: 0; }
          ${phase.peak}% { r: 14; opacity: 0.2; }
          ${phase.hold}% { r: 22; opacity: 0; }
          ${phase.end}%, 100% { r: 0; opacity: 0; }
        }`;
        }).join("")}
      `}</style>
      <svg viewBox={`0 0 ${W} ${viewH}`} className="mx-auto w-full max-w-lg">
        <defs>
          <linearGradient id="pe-area-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={COLOR.sky} stopOpacity="0.2" />
            <stop offset="100%" stopColor={COLOR.sky} stopOpacity="0.02" />
          </linearGradient>
          <linearGradient id="pe-scan-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0" />
            <stop offset="20%" stopColor="currentColor" stopOpacity="0.3" />
            <stop offset="80%" stopColor="currentColor" stopOpacity="0.3" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* ── Factor cards (2×2 grid) ── */}
        {factors.map((f, fi) => {
          const pos = cardPos[fi];
          const cx = pos.x + cardW / 2;
          const cy = pos.y + cardH / 2;
          const evIdx = events.findIndex(e => e.factorIdx === fi);
          const eventPt = evIdx >= 0 ? pts[evIdx + 1] : null;
          return (
            <g key={f.label}>
              <g style={{ animation: `pe-factor${fi} ${dur}s ease-in-out infinite` }}>
                {/* Card */}
                <rect x={pos.x} y={pos.y} width={cardW} height={cardH} rx={12}
                  fill={f.color} fillOpacity={0.05} stroke={f.color} strokeWidth="1" strokeOpacity={0.3} />
                {/* Icon circle */}
                <circle cx={pos.x + 24} cy={cy} r={14} fill={f.color} fillOpacity={0.1} />
                <SvgIcon path={f.icon} x={pos.x + 16} y={cy - iconSize / 2} size={iconSize} color={f.color} />
                {/* Text */}
                <text x={pos.x + 44} y={cy - 4} fontSize="12" fill={f.color} fontWeight="700">{f.label}</text>
                <text x={pos.x + 44} y={cy + 11} fontSize="9" fill="currentColor" fillOpacity={0.45}>{f.sub}</text>
              </g>
              {/* Active glow */}
              <rect x={pos.x - 2} y={pos.y - 2} width={cardW + 4} height={cardH + 4} rx={14}
                fill="none" stroke={f.color} strokeWidth="2"
                style={{ animation: `pe-glow${fi} ${dur}s ease-in-out infinite` }} />
              {/* Connector: card bottom → event point on chart */}
              {eventPt && (
                <g style={{ animation: `pe-conn${fi} ${dur}s ease-in-out infinite` }}>
                  <path
                    d={`M ${cx} ${pos.y + cardH + 2} C ${cx} ${chartTop - 10}, ${eventPt.x} ${chartTop - 10}, ${eventPt.x} ${eventPt.y}`}
                    fill="none" stroke={f.color} strokeWidth="1.5" strokeDasharray="5 4" />
                  <circle cx={eventPt.x} cy={eventPt.y} r={3} fill={f.color} />
                </g>
              )}
            </g>
          );
        })}

        {/* ── Chart area ── */}
        <rect x={chartLeft - 4} y={chartTop - 10} width={chartRight - chartLeft + 8} height={chartH + 20} rx={12}
          fill="currentColor" fillOpacity={0.015} stroke="currentColor" strokeWidth="0.5" strokeOpacity={0.06} />

        {/* Grid */}
        {[0.25, 0.5, 0.75].map(frac => {
          const gy = chartTop + chartH * frac;
          return <line key={frac} x1={chartLeft + 4} y1={gy} x2={chartRight - 4} y2={gy} stroke="currentColor" strokeWidth="0.5" strokeOpacity={0.05} />;
        })}

        {/* Axes */}
        <line x1={chartLeft} y1={chartBottom + 4} x2={chartRight} y2={chartBottom + 4} stroke="currentColor" strokeWidth="0.8" strokeOpacity={0.1} />
        <text x={chartLeft} y={chartTop - 14} fontSize="10" fill="currentColor" fontWeight="600" fillOpacity={0.5}>{t.price}</text>
        <text x={chartRight} y={chartBottom + 18} textAnchor="end" fontSize="8" fill="currentColor" fillOpacity={0.3}>{t.time}</text>

        {/* Area fill */}
        <path d={areaPath} fill="url(#pe-area-grad)"
          style={{ animation: `pe-area ${dur}s ease-in-out infinite` }} />

        {/* Price line */}
        <path d={fullPath} fill="none" stroke={COLOR.sky} strokeWidth="3" strokeLinecap="round"
          strokeDasharray={pathLen}
          style={{ animation: `pe-draw ${dur}s ease-in-out infinite` }} />

        {/* Scan line */}
        <line x1={scanStart} y1={chartTop - 8} x2={scanStart} y2={chartBottom + 4}
          stroke="url(#pe-scan-grad)" strokeWidth="1.5"
          style={{ animation: `pe-scan ${dur}s ease-in-out infinite` }} />

        {/* Event markers */}
        {events.map((ev, i) => {
          const pt = pts[i + 1];
          const up = ev.direction === "up";
          const fc = factors[ev.factorIdx].color;
          const labelY = up ? pt.y - 24 : pt.y + 18;
          return (
            <g key={i} style={{ animation: `pe-evt${i} ${dur}s ease-in-out infinite`, transformOrigin: `${pt.x}px ${pt.y}px` }}>
              {/* Ripple */}
              <circle cx={pt.x} cy={pt.y} r={0} fill="none" stroke={fc} strokeWidth="1.5"
                style={{ animation: `pe-ring${i} ${dur}s ease-out infinite` }} />
              {/* Pill label */}
              <rect x={pt.x - 44} y={labelY - 12} width={88} height={20} rx={10}
                fill={fc} fillOpacity={0.1} stroke={fc} strokeWidth="0.5" strokeOpacity={0.3} />
              <text x={pt.x} y={labelY + 2} textAnchor="middle" fontSize="9.5" fill={fc} fontWeight="700">
                {up ? "▲ " : "▼ "}{ev.label}
              </text>
              {/* Dot */}
              <circle cx={pt.x} cy={pt.y} r={5} className="fill-white dark:fill-zinc-900" stroke={fc} strokeWidth="2" />
            </g>
          );
        })}

        {/* Endpoint pulse */}
        <circle cx={pts[pts.length - 1].x} cy={pts[pts.length - 1].y} r={4}
          fill={COLOR.sky} fillOpacity={0.8}
          style={{ animation: `pe-pulse 1.5s ease-in-out infinite` }} />

        {/* Bottom note */}
        <text x={W / 2} y={viewH - 6} textAnchor="middle" fontSize="10" fill="currentColor" fillOpacity={0.5}>
          {t.reactsEachTime}
        </text>
      </svg>
    </IllustrationCard>
  );
}

// ════════════════════════════════════════════════════════════════════
// 2-2 section 2: 好決算でも下がるのは「期待との差」
// ════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════
// Quarter data and helpers for expectation gap diagram
// ════════════════════════════════════════════════════════════════════

type Quarter = {
  label: string;
  earnings: number;
  expected: number;
};

const QS: Quarter[] = [
  { label: "1Q", earnings: 50, expected: 40 },
  { label: "2Q", earnings: 36, expected: 32 },
  { label: "3Q", earnings: 58, expected: 58 },
  { label: "4Q", earnings: 78, expected: 96 },
];

const GAP_MAX = 110;

export function DocExpectationGapDiagram() {
  const t = STRINGS[useDocLocale()];
  const W = 420;
  const margin = { left: 48, right: 40, top: 14, bottom: 16 };
  const chartW = W - margin.left - margin.right;

  // ── Top: Earnings chart ──
  const eTop = margin.top + 28;
  const eH = 140;
  const eBottom = eTop + eH;

  // ── Gap section ──
  const gapY = eBottom + 32;

  // ── Bottom: Stock price chart ──
  const pTop = gapY + 18;
  const pH = 130;
  const pBottom = pTop + pH;

  const viewH = pBottom + margin.bottom + 30;

  const barSpacing = chartW / QS.length;
  const barW = Math.min(48, barSpacing * 0.5);

  function yE(v: number) {
    return eBottom - (v / GAP_MAX) * (eH - 14) - 6;
  }

  // Expectation line points
  const expectPts = QS.map((q, i) => ({
    x: margin.left + barSpacing * i + barSpacing / 2,
    y: yE(q.expected),
  }));

  const expectPath =
    `M ${expectPts[0].x} ${expectPts[0].y} ` +
    expectPts.slice(1).map((p, i) => {
      const prev = expectPts[i];
      const cpx = (prev.x + p.x) / 2;
      return `C ${cpx} ${prev.y}, ${cpx} ${p.y}, ${p.x} ${p.y}`;
    }).join(" ");

  // Stock price segments
  const priceSegs = [
    { x0: margin.left + 4, y0: pBottom - 22, x1: margin.left + barSpacing * 0.6, y1: pBottom - 56 },
    { x0: margin.left + barSpacing * 0.6, y0: pBottom - 56, x1: margin.left + barSpacing * 1.6, y1: pBottom - 66 },
    { x0: margin.left + barSpacing * 1.6, y0: pBottom - 66, x1: margin.left + barSpacing * 2.6, y1: pBottom - 64 },
    { x0: margin.left + barSpacing * 2.6, y0: pBottom - 64, x1: margin.left + barSpacing * 3.3, y1: pTop + 24 },
    { x0: margin.left + barSpacing * 3.3, y0: pTop + 24, x1: margin.left + barSpacing * 3.6, y1: pTop + 62 },
    { x0: margin.left + barSpacing * 3.6, y0: pTop + 62, x1: W - margin.right - 4, y1: pTop + 66 },
  ];

  // Build full stock price path for area fill
  let pricePath = `M ${priceSegs[0].x0} ${priceSegs[0].y0}`;
  for (const s of priceSegs) {
    const isSharp = s === priceSegs[4];
    if (isSharp) {
      pricePath += ` L ${s.x1} ${s.y1}`;
    } else {
      const cpx = (s.x0 + s.x1) / 2;
      pricePath += ` C ${cpx} ${s.y0}, ${cpx} ${s.y1}, ${s.x1} ${s.y1}`;
    }
  }
  const priceAreaPath = `${pricePath} L ${priceSegs[priceSegs.length - 1].x1} ${pBottom} L ${priceSegs[0].x0} ${pBottom} Z`;

  // Gap result per quarter
  const gaps = QS.map(q => ({
    diff: q.earnings - q.expected,
    beat: q.earnings >= q.expected,
  }));

  return (
    <IllustrationCard
      title={t.gapTitle}
      description={t.gapDescription}
    >
      <svg viewBox={`0 0 ${W} ${viewH}`} className="mx-auto w-full max-w-lg">
        <defs>
          <linearGradient id="eg-area-up" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={COLOR.sky} stopOpacity="0.12" />
            <stop offset="100%" stopColor={COLOR.sky} stopOpacity="0.01" />
          </linearGradient>
          <linearGradient id="eg-area-down" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={COLOR.rose} stopOpacity="0.1" />
            <stop offset="100%" stopColor={COLOR.rose} stopOpacity="0.01" />
          </linearGradient>
          <clipPath id="eg-clip-normal">
            <rect x={margin.left} y={pTop} width={barSpacing * 3.3} height={pH} />
          </clipPath>
          <clipPath id="eg-clip-drop">
            <rect x={margin.left + barSpacing * 3.1} y={pTop} width={W} height={pH} />
          </clipPath>
        </defs>

        {/* ── Earnings section ── */}
        <text x={margin.left} y={eTop - 14} fontSize="11" fill="currentColor" fontWeight="700">{t.earnings}</text>

        {/* Legend */}
        <g transform={`translate(${margin.left + 40}, ${eTop - 22})`}>
          <rect x={0} y={0} width={12} height={10} rx={3} fill="currentColor" fillOpacity={0.12} stroke="currentColor" strokeWidth="1" strokeOpacity={0.2} />
          <text x={16} y={9} fontSize="8.5" fill="currentColor" fillOpacity={0.6}>{t.actualEarnings}</text>
          <line x1={84} y1={5} x2={104} y2={5} stroke={COLOR.sky} strokeWidth="2" strokeDasharray="5 3" />
          <circle cx={94} cy={5} r={3} className="fill-white dark:fill-zinc-900" stroke={COLOR.sky} strokeWidth="1.5" />
          <text x={110} y={9} fontSize="8.5" fill={COLOR.sky} fontWeight="600">{t.marketExpectation}</text>
        </g>

        {/* Baseline */}
        <line x1={margin.left} y1={eBottom} x2={W - margin.right} y2={eBottom} stroke="currentColor" strokeWidth="1" strokeOpacity={0.1} />

        {/* Grid */}
        {[0.33, 0.66].map(f => (
          <line key={f} x1={margin.left} y1={eTop + eH * (1 - f)} x2={W - margin.right} y2={eTop + eH * (1 - f)} stroke="currentColor" strokeWidth="0.5" strokeOpacity={0.05} />
        ))}

        {/* Expectation line */}
        <path d={expectPath} fill="none" stroke={COLOR.sky} strokeWidth="2" strokeDasharray="6 4" />
        {expectPts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={4.5} className="fill-white dark:fill-zinc-900" stroke={COLOR.sky} strokeWidth="2" />
        ))}

        {/* Earnings bars */}
        {QS.map((q, i) => {
          const cx = margin.left + barSpacing * i + barSpacing / 2;
          const earY = yE(q.earnings);
          const expY = yE(q.expected);
          const h = eBottom - earY;
          const beat = q.earnings >= q.expected;
          const isLast = i === QS.length - 1;
          const barColor = isLast && !beat ? COLOR.rose : "currentColor";

          return (
            <g key={q.label}>
              {/* Bar */}
              <rect x={cx - barW / 2} y={earY} width={barW} height={h} rx={6}
                fill={barColor} fillOpacity={0.08}
                stroke={barColor} strokeWidth="1" strokeOpacity={isLast && !beat ? 0.4 : 0.15} />

              {/* 4Q miss — dashed line between expectation and actual, bracket on right */}
              {isLast && !beat && (
                <g>
                  {/* Red dashed line between expected and actual */}
                  <line x1={cx - barW / 2 + 4} y1={expY} x2={cx + barW / 2 - 4} y2={expY}
                    stroke={COLOR.rose} strokeWidth="1.5" strokeDasharray="3 2" />
                  {/* Bracket on right side */}
                  <line x1={cx + barW / 2 + 8} y1={expY} x2={cx + barW / 2 + 8} y2={earY} stroke={COLOR.rose} strokeWidth="1.5" />
                  <line x1={cx + barW / 2 + 4} y1={expY} x2={cx + barW / 2 + 12} y2={expY} stroke={COLOR.rose} strokeWidth="1.5" />
                  <line x1={cx + barW / 2 + 4} y1={earY} x2={cx + barW / 2 + 12} y2={earY} stroke={COLOR.rose} strokeWidth="1.5" />
                  <text x={cx + barW / 2 + 16} y={(expY + earY) / 2 + 1} fontSize="10" fill={COLOR.rose} fontWeight="700" dominantBaseline="central">
                    {t.miss}
                  </text>
                </g>
              )}

              {/* Quarter label */}
              <text x={cx} y={eBottom + 18} textAnchor="middle" fontSize="11" fill="currentColor" fontWeight={isLast ? "700" : "500"}>{q.label}</text>
            </g>
          );
        })}

        {/* ── Gap indicators row ── */}
        <line x1={margin.left} y1={gapY - 6} x2={W - margin.right} y2={gapY - 6} stroke="currentColor" strokeWidth="0.5" strokeOpacity={0.06} />
        {QS.map((_, i) => {
          const cx = margin.left + barSpacing * i + barSpacing / 2;
          const g = gaps[i];
          const color = g.beat ? (g.diff === 0 ? "currentColor" : COLOR.emerald) : COLOR.rose;
          const label = g.diff === 0 ? "±0" : g.diff > 0 ? `+${g.diff}` : `${g.diff}`;
          return (
            <g key={i}>
              <rect x={cx - 22} y={gapY - 4} width={44} height={20} rx={10}
                fill={color} fillOpacity={0.08} stroke={color} strokeWidth="0.8" strokeOpacity={0.25} />
              <text x={cx} y={gapY + 10} textAnchor="middle" fontSize="10" fill={color} fontWeight="700">{label}</text>
            </g>
          );
        })}
        <text x={margin.left - 6} y={gapY + 10} textAnchor="end" fontSize="8" fill="currentColor" fillOpacity={0.4}>{t.gap}</text>

        {/* ── Stock price section ── */}
        <text x={margin.left} y={pTop - 10} fontSize="11" fill="currentColor" fontWeight="700">{t.price}</text>
        <line x1={margin.left} y1={pBottom} x2={W - margin.right} y2={pBottom} stroke="currentColor" strokeWidth="1" strokeOpacity={0.1} />

        {/* Vertical quarter guides */}
        {QS.map((q, i) => {
          const cx = margin.left + barSpacing * i + barSpacing / 2;
          return <line key={q.label} x1={cx} y1={pTop} x2={cx} y2={pBottom} stroke="currentColor" strokeWidth="0.5" strokeDasharray="3 5" strokeOpacity={0.08} />;
        })}

        {/* Price area fill — split into normal and drop zones */}
        <path d={priceAreaPath} fill="url(#eg-area-up)" clipPath="url(#eg-clip-normal)" />
        <path d={priceAreaPath} fill="url(#eg-area-down)" clipPath="url(#eg-clip-drop)" />

        {/* Price line segments */}
        {priceSegs.map((s, i) => {
          const isDropZone = i >= 4;
          const isSharp = i === 4;
          const cpx = (s.x0 + s.x1) / 2;
          const d = isSharp
            ? `M ${s.x0} ${s.y0} L ${s.x1} ${s.y1}`
            : `M ${s.x0} ${s.y0} C ${cpx} ${s.y0}, ${cpx} ${s.y1}, ${s.x1} ${s.y1}`;
          return <path key={i} d={d} fill="none" stroke={isDropZone ? COLOR.rose : COLOR.sky} strokeWidth="3" strokeLinecap="round" />;
        })}

        {/* Endpoint */}
        <circle cx={priceSegs[priceSegs.length - 1].x1} cy={priceSegs[priceSegs.length - 1].y1} r={5}
          className="fill-white dark:fill-zinc-900" stroke={COLOR.rose} strokeWidth="2" />

        {/* 4Q drop highlight zone */}
        <rect x={margin.left + barSpacing * 3.05} y={pTop + 8} width={barSpacing * 0.85} height={pH - 20} rx={8}
          fill={COLOR.rose} fillOpacity={0.04} stroke={COLOR.rose} strokeWidth="0.5" strokeOpacity={0.1} />
        <text x={margin.left + barSpacing * 3.5} y={pBottom - 10} textAnchor="middle" fontSize="10" fill={COLOR.rose} fontWeight="700">
          {t.missToDrop}
        </text>

        {/* Bottom note */}
        <text x={W / 2} y={viewH - 8} textAnchor="middle" fontSize="10" fill="currentColor" fillOpacity={0.5}>
          {t.gapBottomNote}
        </text>
      </svg>
    </IllustrationCard>
  );
}
